import { FC } from 'react'
import { Menu } from '@reapit/elements'
import { NavigationMenu } from '../types'
import { useNavigationHandler } from '../utils'

interface MainSubMenuItemProps extends NavigationMenu {}

/**
 * `MainSubMenuItem` renders a single sub-navigation item inside a `TopBar.NavMenuItem`.
 *
 * It:
 * - Accepts navigation props from the `NavigationMenu` type (including `title`, `href`, and `onClick`)
 * - Uses the `useNavigationHandler` hook to handle navigation logic and determine active state
 * - Forwards navigation behavior and active styling to `<Menu.AnchorItem>` from Reapit Elements
 *
 * @component
 * @param {NavigationMenu} props - The menu item configuration, including:
 * @param {() => void} [props.onClick] - Optional callback invoked when the menu item is clicked.
 * @param {string} props.title - The text to display for the menu item.
 * @param {string} props.href - The navigation target (route path or URL).
 * @returns A styled, navigable `<Menu.AnchorItem>` component with proper active state and routing behavior.
 */
export const MainSubMenuItem: FC<MainSubMenuItemProps> = ({ onClick, title, href }) => {
  const navHandler = useNavigationHandler(href, onClick)
  return <Menu.AnchorItem {...navHandler}>{title}</Menu.AnchorItem>
}
